// ============================================================
// Příjezdy a nakládky — dopravci, dnešní plán nakládek, urgence.
// All carriers, drivers and orders are fictional demo data.
// Times are relative to today; the demo clock starts at 12:55.
// Story items K-01 … K-07 are referenced by the urgency simulation
// and by the demo script — keep their IDs stable.
// ============================================================

// ISO timestamp for today (or today + dayOffset) at hh:mm local time.
export function at(hhmm, dayOffset = 0) {
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

export const DEMO_START = '12:55';
export const URGENCY_CHECK = '13:00';

// ── Carriers ────────────────────────────────────────────────
export const carriers = [
  {
    id: 'D1', name: 'Karpaty Trans Sp. z o.o.', country: 'PL', fleet: 40, channel: 'sms', lang: 'pl',
    dispatcher: { name: 'Agnieszka Malinowska', phone: '+48 604 218 337', email: 'dyspozytor@karpatytrans.pl' },
  },
  {
    id: 'D2', name: 'Śląsk Przewozy Sp. z o.o.', country: 'PL', fleet: 32, channel: 'whatsapp', lang: 'pl',
    dispatcher: { name: 'Bartosz Wróbel', phone: '+48 512 904 661', email: 'spedycja@slaskprzewozy.pl' },
  },
  {
    id: 'D3', name: 'Opolski Transport Sp. j.', country: 'PL', fleet: 30, channel: 'email', lang: 'pl',
    dispatcher: { name: 'Ewa Kaczmarek', phone: '+48 77 441 20 15', email: 'logistyka@opolskitransport.pl' },
  },
  {
    id: 'D4', name: 'Jesenická doprava s.r.o.', country: 'CZ', fleet: 12, channel: 'phone', lang: 'cs',
    dispatcher: { name: 'Dispečink Jeseník', phone: '+420 584 401 220', email: 'dispecink@jesdoprava.cz' },
  },
  {
    id: 'D5', name: 'Moravia Cargo s.r.o.', country: 'CZ', fleet: 6, channel: 'sms', lang: 'cs',
    dispatcher: { name: 'Dispečink Šumperk', phone: '+420 731 552 908', email: 'info@moraviacargo.cz' },
  },
];

export const getCarrier = (id) => carriers.find(c => c.id === id) || null;

// ── Loading states ──────────────────────────────────────────
// Order = priority in the overview (needs attention first).
export const loadingStates = [
  'no-info', 'not-coming', 'no-power', 'rescheduled', 'confirmed', 'en-route', 'on-site', 'loaded',
];

export const attentionStates = ['no-info', 'not-coming'];

// ── Filler data ─────────────────────────────────────────────
const customers = [
  'Papírny Ostrava a.s.', 'Farby Śląskie S.A.', 'Plasty Zlín s.r.o.', 'Tynk Polska Sp. z o.o.',
  'Agro Haná a.s.', 'Kabely Morava s.r.o.', 'Chemia Opole S.A.', 'Stavební hmoty Brno a.s.',
];
const materials = [
  { grade: 'Omyacarb 2-AV', type: 'silo', weight: 26 },
  { grade: 'Omyacarb 5-AV', type: 'silo', weight: 25 },
  { grade: 'Omyacarb 10-AV', type: 'silo', weight: 27 },
  { grade: 'Omyacarb 40-GU', type: 'silo', weight: 26 },
  { grade: 'Omyacarb 15-GU (big bag)', type: 'pallet', weight: 24 },
  { grade: 'Omyacarb 130-AL (25 kg)', type: 'pallet', weight: 22 },
];
const plDrivers = [
  'Krzysztof Nowak', 'Marek Wiśniewski', 'Tomasz Kowalczyk', 'Paweł Zieliński', 'Andrzej Lewandowski',
  'Piotr Wójcik', 'Grzegorz Kamiński', 'Łukasz Szymański', 'Michał Dąbrowski', 'Jacek Kozłowski',
  'Rafał Jankowski', 'Dariusz Mazur', 'Wojciech Krawczyk', 'Sebastian Pawlak',
];
const czDrivers = ['Jiří Kubíček', 'Radek Vávra', 'Milan Holub', 'Zdeněk Pospíšil'];
const plPlates = ['SK', 'SO', 'SB', 'SOP', 'SGL', 'SRB', 'SPS'];

let seq = 0;
function filler({ carrierId, planned, state, eta, gate }) {
  seq += 1;
  const carrier = getCarrier(carrierId);
  const isPl = carrier.country === 'PL';
  const material = materials[seq % materials.length];
  const drivers = isPl ? plDrivers : czDrivers;
  const plate = isPl
    ? `${plPlates[seq % plPlates.length]} ${String(1000 + seq * 373).slice(-4)}${'KMXTV'[seq % 5]}`
    : `${(seq % 8) + 1}M${seq % 9} ${String(2000 + seq * 517).slice(-4)}`;
  return {
    id: `L-${String(seq).padStart(3, '0')}`,
    carrierId,
    plate,
    driver: { name: drivers[seq % drivers.length], phone: null },
    orderId: `DOD 8045${String(1200 + seq * 7).padStart(4, '0')}`,
    customer: customers[seq % customers.length],
    material: material.grade,
    type: material.type,
    weight: material.weight,
    gate: gate || (material.type === 'silo' ? 'CLS' : 'PST'),
    plannedAt: at(planned),
    eta: eta ? at(eta) : null,
    state,
    power: null,
    gpsKm: null,
    lastContact: null,
    messages: [],
  };
}

// 21 loadings planned this morning (peaks 6–9 and 12–13), 19 of them done.
const loadedTimes = [
  '06:00', '06:15', '06:30', '06:45', '07:00', '07:10', '07:30', '07:45', '08:00', '08:20', '08:40',
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:10', '12:30', '12:40',
];
const loadedCarriers = ['D1', 'D2', 'D3', 'D1', 'D4', 'D2', 'D3', 'D1', 'D3', 'D2', 'D4', 'D1', 'D3', 'D2', 'D1', 'D3', 'D4', 'D1', 'D2', 'D3', 'D5'];
const loaded = loadedTimes.map((time, i) => filler({ carrierId: loadedCarriers[i], planned: time, state: 'loaded', eta: time }));
// The last two of the morning are still on site (#115): one stuck at CLS, one on the weighbridge.
Object.assign(loaded[19], { state: 'on-site', eta: at('12:02'), site: { spot: 'loading', since: at('12:15') } });
Object.assign(loaded[20], { state: 'on-site', eta: at('12:24'), site: { spot: 'weighbridge', since: at('12:52') } });

// Informed (not yet loaded): confirmed, en route with GPS, on site.
const informed = [
  filler({ carrierId: 'D3', planned: '13:00', state: 'on-site', eta: '12:48' }),
  filler({ carrierId: 'D1', planned: '13:30', state: 'en-route', eta: '13:25' }),
  filler({ carrierId: 'D2', planned: '14:00', state: 'confirmed', eta: '14:00' }),
  filler({ carrierId: 'D3', planned: '14:30', state: 'en-route', eta: '14:10' }),
  filler({ carrierId: 'D1', planned: '15:00', state: 'confirmed', eta: '15:15' }),
  filler({ carrierId: 'D4', planned: '16:00', state: 'confirmed', eta: '16:00' }),
  filler({ carrierId: 'D2', planned: '16:30', state: 'confirmed', eta: '16:45' }),
  filler({ carrierId: 'D3', planned: '17:30', state: 'confirmed', eta: '17:30' }),
];
informed[0].site = { spot: 'loading', since: at('12:51') };
informed[1].gpsKm = 12;
informed[3].gpsKm = 64;

// ── Story items ─────────────────────────────────────────────
function story(id, data) {
  const carrier = getCarrier(data.carrierId);
  return {
    gate: data.type === 'silo' ? 'CLS' : 'PST',
    eta: null,
    power: null,
    gpsKm: null,
    lastContact: null,
    messages: [],
    customer: customers[id.charCodeAt(3) % customers.length],
    driver: { name: data.driverName, phone: carrier.country === 'PL' ? '+48 6xx xxx xxx' : '+420 6xx xxx xxx' },
    ...data,
    id,
    storyId: id,
  };
}

export const storyLoadings = [
  // K-01 — no answer to WhatsApp nor SMS → voice call to dispatcher → tomorrow 07:00.
  story('K-01', {
    carrierId: 'D2', plate: 'SK 7310M', driverName: 'Rafał Jankowski', orderId: 'DOD 80452311',
    material: 'Omyacarb 2-AV', type: 'silo', weight: 26, plannedAt: at('13:30'), state: 'no-info',
  }),
  // K-02 — driver answers via link: out of driving hours, can load from 05:30.
  story('K-02', {
    carrierId: 'D1', plate: 'SO 4417T', driverName: 'Grzegorz Kamiński', orderId: 'DOD 80452318',
    material: 'Omyacarb 10-AV', type: 'silo', weight: 27, plannedAt: at('14:00'), state: 'no-info',
  }),
  // K-03 — GPS shows 48 km away → no urgency needed.
  story('K-03', {
    carrierId: 'D3', plate: 'SOP 2208K', driverName: 'Piotr Wójcik', orderId: 'DOD 80452322',
    material: 'Omyacarb 5-AV', type: 'silo', weight: 25, plannedAt: at('14:30'), state: 'no-info',
  }),
  // K-04 — SMS reply in Polish „TAK, 15:30“ → confirmed.
  story('K-04', {
    carrierId: 'D1', plate: 'SB 9051X', driverName: 'Tomasz Kowalczyk', orderId: 'DOD 80452327',
    material: 'Omyacarb 40-GU', type: 'silo', weight: 26, plannedAt: at('15:00'), state: 'no-info',
  }),
  // K-05 — pallets, no reply even after escalation → handed to planner.
  story('K-05', {
    carrierId: 'D5', plate: '7M2 5530', driverName: 'Radek Vávra', orderId: 'DOD 80452334',
    material: 'Omyacarb 15-GU (big bag)', type: 'pallet', weight: 24, plannedAt: at('15:30'), state: 'no-info',
  }),
  // K-06 — already on site, waiting for CLS: urgency targets only those who are missing.
  story('K-06', {
    carrierId: 'D4', plate: '8M1 2264', driverName: 'Milan Holub', orderId: 'DOD 80452301',
    material: 'Omyacarb 2-AV', type: 'silo', weight: 26, plannedAt: at('12:45'), eta: at('12:41'),
    state: 'on-site', lastContact: at('12:41'), site: { spot: 'queue', since: at('12:43') },
    messages: [{ time: at('12:41'), dir: 'system', channel: 'system', text: 'Průjezd bránou CLS, vjezdové vážení 14,2 t' }],
  }),
  // Three more without info that answer during the urgency.
  story('K-08', {
    carrierId: 'D3', plate: 'SGL 6612V', driverName: 'Michał Dąbrowski', orderId: 'DOD 80452340',
    material: 'Omyacarb 2-AV', type: 'silo', weight: 26, plannedAt: at('15:00'), state: 'no-info',
  }),
  story('K-09', {
    carrierId: 'D2', plate: 'SRB 3390T', driverName: 'Jacek Kozłowski', orderId: 'DOD 80452346',
    material: 'Omyacarb 130-AL (25 kg)', type: 'pallet', weight: 22, plannedAt: at('16:00'), state: 'no-info',
  }),
  story('K-10', {
    carrierId: 'D4', plate: '8M4 1187', driverName: 'Jiří Kubíček', orderId: 'DOD 80452351',
    material: 'Omyacarb 5-AV', type: 'silo', weight: 25, plannedAt: at('16:30'), state: 'no-info',
  }),
];

// K-07 is not in the seed: the simulation creates it from a new SAP order.
export const newSapLoading = story('K-07', {
  carrierId: 'D3', plate: 'SO 5182M', driverName: 'Łukasz Szymański', orderId: 'DOD 80452362',
  material: 'Omyacarb 10-AV', type: 'silo', weight: 27, plannedAt: at('16:30'), state: 'no-info',
});

export const initialLoadings = [...loaded, ...informed, ...storyLoadings];

// Number of loadings per state, e.g. { 'no-info': 8, loaded: 21, ... }.
export function countByState(loadings) {
  const counts = Object.fromEntries(loadingStates.map(s => [s, 0]));
  loadings.forEach(l => { counts[l.state] += 1; });
  return counts;
}

// ── Urgency message templates ───────────────────────────────
export function urgencyText(loading) {
  const carrier = getCarrier(loading.carrierId);
  // Message language follows the carrier, not the UI.
  const locale = carrier.lang === 'pl' ? 'pl-PL' : 'cs-CZ';
  const time = new Date(loading.plannedAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (carrier.lang === 'pl') {
    return {
      text: `OMYA Pomezí: dziś czekamy na ${loading.plate} (${loading.orderId}, ${loading.material}) ok. ${time}. Czy przyjedzie? Odpowiedz: TAK + godzina / NIE / link: omya.link/k/${loading.id}`,
      textCs: `OMYA Pomezí: dnes čekáme ${loading.plate} (${loading.orderId}, ${loading.material}) kolem ${time}. Přijede? Odpovězte: ANO + čas / NE / odkaz.`,
    };
  }
  return {
    text: `OMYA Pomezí: dnes čekáme ${loading.plate} (${loading.orderId}, ${loading.material}) kolem ${time}. Přijede? Odpovězte ANO + čas / NE, nebo omya.link/k/${loading.id}`,
    textCs: null,
  };
}
